import React from 'react';

// Grade 6 Simulations
import {
  SolarSystemOrbit,
  OrbitSandbox,
  AsteroidComet,
  SunVideo,
  MarsGlobe,
  JupiterLayers,
  MoonOrbit,
  EclipseAligner,
  GoldilocksZone,
  MilkyWayFlythrough
} from './g6';

// Grade 7 Simulations
import {
  EMSineWave,
  PhotonTimeline,
  BlackbodyCurve,
  AtmosphereScatter,
  TelescopePhotons,
  RayTracing,
  AtmosphericDistortion,
  EMSpectrumAbsorption,
  EarthTiltOrbital,
  SeasonToggles,
  TidalBulge3D,
  TidalDragInteractive,
  SolarFlareCME,
  MagnetosphereAurora,
  FrostLineZones,
  GasGiantSweep,
  SaturnRingsFlythrough,
  RocheLimit,
  NebulaCollapse,
  StarIgnition,
  OrbitalMassScale,
  NewtonGravityArrows
} from './g7';

// Grade 8 Simulations
import { GravityParticleCollapse } from './grade8/GravityParticleCollapse';
import { OrbitVisualizer } from './grade8/OrbitVisualizer';
import { FusionCollider } from './grade8/FusionCollider';
import { MassDefectAnimation } from './grade8/MassDefectAnimation';
import { NebulaMolecularCloud } from './grade8/NebulaMolecularCloud';
import { ProtostarsIgnition } from './grade8/ProtostarsIgnition';
import { GravityPressureBalance } from './grade8/GravityPressureBalance';
import { HydrostaticEquilibrium } from './grade8/HydrostaticEquilibrium';
import { MassCoreTempSlider } from './grade8/MassCoreTempSlider';
import { FuelGaugeComparison } from './grade8/FuelGaugeComparison';
import { RedGiantExpansion } from './grade8/RedGiantExpansion';
import { PlanetaryNebulaEjection } from './grade8/PlanetaryNebulaEjection';
import { WhiteDwarf3D } from './grade8/WhiteDwarf3D';
import { ElectronDegeneracy } from './grade8/ElectronDegeneracy';
import { OnionLayers } from './grade8/OnionLayers';
import { SupernovaCollapse } from './grade8/SupernovaCollapse';
import { ElectronProtonCrush } from './grade8/ElectronProtonCrush';
import { NeutronStarCitySize } from './grade8/NeutronStarCitySize';
import { PulsarMagneticField } from './grade8/PulsarMagneticField';
import { PulsarBeamSweep } from './grade8/PulsarBeamSweep';
import { SpacetimeWarp } from './grade8/SpacetimeWarp';
import { EscapeVelocityChart } from './grade8/EscapeVelocityChart';
import { BlackHoleOrbitTracker } from './grade8/BlackHoleOrbitTracker';
import { AccretionDisk } from './grade8/AccretionDisk';

// Generic Simulations
import { OrbitSimulation } from './OrbitSimulation';
import { WaveSimulation } from './WaveSimulation';
import { FieldSimulation } from './FieldSimulation';
import { ExplosionSimulation } from './ExplosionSimulation';
import { PhaseSimulation } from './PhaseSimulation';
import { ScaleSimulation } from './ScaleSimulation';
import { StructureSimulation } from './StructureSimulation';
import { ParticleSimulation } from './ParticleSimulation';
import { PhysicsConceptSimulation } from './PhysicsConceptSimulation';

interface SimulationRendererProps {
  lessonSlug: string;   // e.g. "astro-g8-l01"
  pageIndex: number;    // 0-based index of the current page within the lesson
  pageData?: any;       // optional current page object
  size?: number;        // size of the canvas container, defaults to 380
}

export function SimulationRenderer({ lessonSlug, pageIndex, pageData, size = 380 }: SimulationRendererProps) {
  if (lessonSlug.startsWith('phys-')) {
    return <PhysicsConceptSimulation lessonSlug={lessonSlug} pageIndex={pageIndex} pageData={pageData} size={size} />;
  }

  // 1. Check custom Grade 6 simulations
  if (lessonSlug.startsWith('astro-g6-')) {
    switch (`${lessonSlug}-p${pageIndex}`) {
      case 'astro-g6-l01-p0': return <SolarSystemOrbit width={size} height={size} />;
      case 'astro-g6-l02-p0': return <OrbitSandbox width={size} height={size} />;
      case 'astro-g6-l03-p0': return <AsteroidComet width={size} height={size} />;
      case 'astro-g6-l04-p0': return <SunVideo width={size} height={size} />;
      case 'astro-g6-l05-p0': return <MarsGlobe width={size} height={size} />;
      case 'astro-g6-l06-p0': return <JupiterLayers width={size} height={size} />;
      case 'astro-g6-l07-p0': return <MoonOrbit width={size} height={size} />;
      case 'astro-g6-l08-p0': return <EclipseAligner width={size} height={size} />;
      case 'astro-g6-l09-p0': return <GoldilocksZone width={size} height={size} />;
      case 'astro-g6-l10-p0': return <MilkyWayFlythrough width={size} height={size} />;
      default: break;
    }
  }

  // 2. Check custom Grade 7 simulations
  if (lessonSlug.startsWith('astro-g7-')) {
    switch (`${lessonSlug}-p${pageIndex}`) {
      case 'astro-g7-l01-p0': return <EMSineWave width={size} height={size} />;
      case 'astro-g7-l01-p4': return <PhotonTimeline width={size} height={size} />;
      case 'astro-g7-l02-p0': return <BlackbodyCurve width={size} height={size} />;
      case 'astro-g7-l02-p4': return <AtmosphereScatter width={size} height={size} />;
      case 'astro-g7-l03-p0': return <TelescopePhotons width={size} height={size} />;
      case 'astro-g7-l03-p4': return <RayTracing width={size} height={size} />;
      case 'astro-g7-l04-p0': return <AtmosphericDistortion width={size} height={size} />;
      case 'astro-g7-l04-p4': return <EMSpectrumAbsorption width={size} height={size} />;
      case 'astro-g7-l05-p0': return <EarthTiltOrbital width={size} height={size} />;
      case 'astro-g7-l05-p4': return <SeasonToggles width={size} height={size} />;
      case 'astro-g7-l06-p0': return <TidalBulge3D width={size} height={size} />;
      case 'astro-g7-l06-p4': return <TidalDragInteractive width={size} height={size} />;
      case 'astro-g7-l07-p0': return <SolarFlareCME width={size} height={size} />;
      case 'astro-g7-l07-p4': return <MagnetosphereAurora width={size} height={size} />;
      case 'astro-g7-l08-p0': return <FrostLineZones width={size} height={size} />;
      case 'astro-g7-l08-p4': return <GasGiantSweep width={size} height={size} />;
      case 'astro-g7-l09-p0': return <SaturnRingsFlythrough width={size} height={size} />;
      case 'astro-g7-l09-p4': return <RocheLimit width={size} height={size} />;
      case 'astro-g7-l10-p0': return <NebulaCollapse width={size} height={size} />;
      case 'astro-g7-l10-p4': return <StarIgnition width={size} height={size} />;
      case 'astro-g7-l11-p0': return <OrbitalMassScale width={size} height={size} />;
      case 'astro-g7-l11-p4': return <NewtonGravityArrows width={size} height={size} />;
      default: break;
    }
  }

  // 3. Check custom Grade 8 simulations
  if (lessonSlug.startsWith('astro-g8-')) {
    switch (`${lessonSlug}-p${pageIndex}`) {
      case 'astro-g8-l01-p0': return <GravityParticleCollapse />;
      case 'astro-g8-l01-p1': return <OrbitVisualizer />;
      case 'astro-g8-l02-p0': return <FusionCollider />;
      case 'astro-g8-l02-p1': return <MassDefectAnimation />;
      case 'astro-g8-l03-p0': return <NebulaMolecularCloud />;
      case 'astro-g8-l03-p1': return <ProtostarsIgnition />;
      case 'astro-g8-l04-p0': return <GravityPressureBalance />;
      case 'astro-g8-l04-p1': return <HydrostaticEquilibrium />;
      case 'astro-g8-l05-p0': return <MassCoreTempSlider />;
      case 'astro-g8-l05-p1': return <FuelGaugeComparison />;
      case 'astro-g8-l06-p0': return <RedGiantExpansion />;
      case 'astro-g8-l06-p1': return <PlanetaryNebulaEjection />;
      case 'astro-g8-l07-p0': return <WhiteDwarf3D />;
      case 'astro-g8-l07-p1': return <ElectronDegeneracy />;
      case 'astro-g8-l08-p0': return <OnionLayers />;
      case 'astro-g8-l08-p1': return <SupernovaCollapse />;
      case 'astro-g8-l09-p0': return <ElectronProtonCrush />;
      case 'astro-g8-l09-p1': return <NeutronStarCitySize />;
      case 'astro-g8-l10-p0': return <PulsarMagneticField />;
      case 'astro-g8-l10-p1': return <PulsarBeamSweep />;
      case 'astro-g8-l11-p0': return <SpacetimeWarp />;
      case 'astro-g8-l11-p1': return <EscapeVelocityChart />;
      case 'astro-g8-l12-p0': return <BlackHoleOrbitTracker />;
      case 'astro-g8-l12-p1': return <AccretionDisk />;
      default: break;
    }
  }

  // 4. Default case: dynamically select a generic simulation based on keywords
  if (!pageData || !pageData.simulation) return null;

  const desc = (pageData.simulation.description || '').toLowerCase();
  const title = (pageData.title || '').toLowerCase();
  const content = (pageData.content || '').toLowerCase();
  const text = `${title} ${desc} ${content}`;

  // Orbit / Gravitational / Alignment mechanics
  if (
    text.includes('orbit') ||
    text.includes('revolve') ||
    text.includes('planetary') ||
    text.includes('solar system') ||
    text.includes('gravity') ||
    text.includes('gravitational') ||
    text.includes('axis') ||
    text.includes('transit') ||
    text.includes('wobble') ||
    text.includes('eclipse') ||
    text.includes('kepler') ||
    text.includes('coronagraph') ||
    text.includes('binary') ||
    text.includes('radial velocity')
  ) {
    let planetColors = ['#60A5FA', '#34D399', '#F87171', '#A78BFA'];
    if (text.includes('mars')) planetColors = ['#EF4444'];
    else if (text.includes('jupiter')) planetColors = ['#F59E0B'];
    
    return (
      <OrbitSimulation
        size={size}
        bodyCount={text.includes('moon') ? 1 : 4}
        starColor="#F59E0B"
        planetColors={planetColors}
        speedMultiplier={1}
      />
    );
  }

  // Wave / Light / Spectra / Interferometry mechanics
  if (
    text.includes('wave') ||
    text.includes('light') ||
    text.includes('photon') ||
    text.includes('frequency') ||
    text.includes('wavelength') ||
    text.includes('electromagnetic') ||
    text.includes('radiation') ||
    text.includes('color') ||
    text.includes('absorption') ||
    text.includes('emission') ||
    text.includes('spectr') ||
    text.includes('doppler') ||
    text.includes('redshift') ||
    text.includes('blueshift') ||
    text.includes('spectroscopy') ||
    text.includes('prism') ||
    text.includes('interferometer') ||
    text.includes('interference') ||
    text.includes('hologram') ||
    text.includes('laser')
  ) {
    let color = '#3B82F6';
    if (text.includes('red')) color = '#EF4444';
    else if (text.includes('blue')) color = '#60A5FA';
    else if (text.includes('green')) color = '#10B981';
    
    return (
      <WaveSimulation
        size={size}
        frequency={text.includes('high') ? 5 : 3}
        amplitude={30}
        color={color}
        waveCount={3}
      />
    );
  }

  // Field / Magnetic / Flux mechanics
  if (
    text.includes('field') ||
    text.includes('magnetic') ||
    text.includes('dipole') ||
    text.includes('magnetosphere') ||
    text.includes('charge') ||
    text.includes('aurora') ||
    text.includes('magnet') ||
    text.includes('flux')
  ) {
    return (
      <FieldSimulation
        size={size}
        color="#10B981"
        lineCount={12}
        direction={text.includes('dipole') ? 'dipole' : 'radial'}
      />
    );
  }

  // Explosion / Supernova / Flare mechanics
  if (
    text.includes('explosion') ||
    text.includes('supernova') ||
    text.includes('blast') ||
    text.includes('collapse') ||
    text.includes('nuclear') ||
    text.includes('fusion') ||
    text.includes('ignition') ||
    text.includes('flare') ||
    text.includes('burst') ||
    text.includes('detonation') ||
    text.includes('erupt') ||
    text.includes('nova')
  ) {
    return (
      <ExplosionSimulation
        size={size}
        color="#EF4444"
        particleCount={50}
        repeat={true}
      />
    );
  }

  // Phase / Heat / Recombination / Vapor states
  if (
    text.includes('phase') ||
    text.includes('state') ||
    text.includes('solid') ||
    text.includes('liquid') ||
    text.includes('gas') ||
    text.includes('plasma') ||
    text.includes('melt') ||
    text.includes('freeze') ||
    text.includes('temperature') ||
    text.includes('heat') ||
    text.includes('cool') ||
    text.includes('hot') ||
    text.includes('vapor') ||
    text.includes('thermal') ||
    text.includes('ice') ||
    text.includes('condensation') ||
    text.includes('recombination')
  ) {
    const stages = [
      { label: 'Solid', color: '#60A5FA' },
      { label: 'Liquid', color: '#34D399' },
      { label: 'Gas', color: '#F59E0B' }
    ];
    return (
      <PhaseSimulation
        size={size}
        stages={stages}
        animated={true}
      />
    );
  }

  // Scale / Size / Universe / Distance comparisons
  if (
    text.includes('scale') ||
    text.includes('size') ||
    text.includes('compare') ||
    text.includes('distance') ||
    text.includes('universe') ||
    text.includes('dimension') ||
    text.includes('metric') ||
    text.includes('infinite') ||
    text.includes('expansion') ||
    text.includes('expand') ||
    text.includes('horizon')
  ) {
    const objects = [
      { label: 'Earth', size: 10, color: '#3B82F6' },
      { label: 'Jupiter', size: 50, color: '#F59E0B' },
      { label: 'Sun', size: 120, color: '#EF4444' }
    ];
    return (
      <ScaleSimulation
        size={size}
        objects={objects}
      />
    );
  }

  // Structure / Layers / Composition / Cross-section profiles
  if (
    text.includes('structure') ||
    text.includes('layer') ||
    text.includes('core') ||
    text.includes('mantle') ||
    text.includes('crust') ||
    text.includes('composition') ||
    text.includes('anatomy') ||
    text.includes('interior') ||
    text.includes('cross-section') ||
    text.includes('filament') ||
    text.includes('web') ||
    text.includes('geometry') ||
    text.includes('profile')
  ) {
    const layers = [
      { label: 'Core', color: '#EF4444' },
      { label: 'Mantle', color: '#F59E0B' },
      { label: 'Crust', color: '#3B82F6' }
    ];
    return (
      <StructureSimulation
        size={size}
        layers={layers}
      />
    );
  }

  // Explicit Particle checks
  if (
    text.includes('particle') ||
    text.includes('atom') ||
    text.includes('electron') ||
    text.includes('proton') ||
    text.includes('neutron') ||
    text.includes('neutrino') ||
    text.includes('matter') ||
    text.includes('wind') ||
    text.includes('collision') ||
    text.includes('wimp') ||
    text.includes('axion')
  ) {
    return (
      <ParticleSimulation
        size={size}
        count={50}
        color="#A78BFA"
        particleSize={4}
        speed={2}
      />
    );
  }

  // Default fallback: ParticleSimulation
  return (
    <ParticleSimulation
      size={size}
      count={40}
      color="#8B5CF6"
      particleSize={4}
      speed={1.5}
    />
  );
}
